import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service } from 'webapp-types';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import MetricsUnusedListComponent from 'webapp/components/metrics/unused/list';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

import CustomQuery from 'webapp/models-custom/custom-query';
import type { MetricType } from 'webapp/models-custom/filter-sort/metric-unused';
import HorizonMetric from 'webapp/models-custom/horizon-metric';
import Script from 'webapp/models-custom/script';

export type UnusedItemModel = CustomQuery | Script | HorizonMetric;

export default class MetricsUnusedPopupComponent extends PopupInnerModalComponent<PopupData.Metrics.UnusedCleanup> {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('model-manager/horizon-metrics')
	declare mmHorizonMetricsService: Service.ModelManager.HorizonMetrics;

	@tracked selected: UnusedItemModel[] = [];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		void Promise.allSettled([
			this.mmMetricsService.requestMetrics(),
			this.mmCustomQueriesService.requestCustomQueries(),
			this.mmScriptsService.requestScripts(),
			this.mmHorizonMetricsService.requestHorizonMetrics(),
		]).then(() => {
			this.scheduleReposition();
		});
	}

	get selectedCount(): number {
		return this.selected.length;
	}

	get isDeleteBtnDisabled(): boolean {
		return !this.selectedCount;
	}

	get deleteBtnLabel(): string {
		const selectedCount = this.selectedCount;
		let label = 'Delete selected';

		if (selectedCount) {
			label += ` (${selectedCount})`;
		}

		return label;
	}

	get initialMetricType(): MetricType | undefined {
		return (this.args.model.data as PopupData.Metrics.UnusedCleanup)?.initialMetricType;
	}

	@action
	deleteSelected(): void {
		const count = this.selectedCount;

		this.popupService.openConfirmPopup({
			onConfirm: () => {
				this.performDelete();
			},
			title: `Are you sure you want to delete ${count} selected custom metric${count !== 1 ? 's' : ''}?`,
			text: '',
		});
	}

	private performDelete(): void {
		const deleteEntries: { item: UnusedItemModel; promise: Promise<unknown> }[] = [];

		this.selected.forEach((item) => {
			let url;

			if (item instanceof Script) {
				url = `/scripts/${item.id}`;
			} else if (item instanceof CustomQuery) {
				url = `/customqueries/${item.id}`;
			} else if (item instanceof HorizonMetric) {
				url = `/horizon-metrics/${item.id}`;
			}

			if (!url) {
				return;
			}

			deleteEntries.push({
				item,
				promise: this.requestService.api('DELETE', url, null, {
					skipShowingErrorInfolayer: true,
				}),
			});
		});

		if (deleteEntries.length) {
			this.submitInProgress = true;

			void Promise.allSettled(deleteEntries.map((e) => e.promise)).then(
				(statusArray: PromiseSettledResult<unknown>[]) => {
					const rejectedRequests = statusArray.filter(
						(item) => item.status === 'rejected',
					);

					statusArray.forEach((result, index) => {
						if (result.status === 'fulfilled') {
							try {
								const record = deleteEntries[index]!.item.model;
								record.deleteRecord();
								later(() => {
									try {
										record.unloadRecord();
									} catch (_e) {
										// record may already be unloaded
									}
								}, 5);
							} catch (_e) {
								// record may already be unloaded
							}
						}
					});

					if (rejectedRequests.length) {
						this.infolayerService.error(
							'There was a problem deleting some of your selected metrics.',
						);
					} else {
						this.infolayerService.ok('Selected metrics have been deleted!');
					}

					this.submitInProgress = false;
					this.close();
				},
			);
		}
	}

	@action
	changeSelected(selected: UnusedItemModel[]): void {
		this.selected = selected;
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="Purge" @size="lg" />
			<span>Clean-up unused Custom metrics</span>
		</div>
		<div class="content">
			<MetricsUnusedListComponent
				@initialMetricType={{this.initialMetricType}}
				@onChangeSelected={{this.changeSelected}}
				@onRecenter={{this.scheduleReposition}}
			/>

			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text={{this.deleteBtnLabel}}
						@extraClass="delete"
						@icon="Trash"
						@theme="red"
						@isDisabled={{this.isDeleteBtnDisabled}}
						@submitInProgress={{this.submitInProgress}}
						@onTrigger={{this.deleteSelected}}
					/>
				</div>
			</div>
		</div>
	</template>
}
